import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

type BuyerType = 'Utility' | 'Consumer';

type ContractMappingOption = {
  code: string;
  name: string;
};

type ContractMappingRow = {
  id: number;
  generatingCompanyCode: string;
  generatingCompanyName: string;
  generatingUnitCode: string;
  generatingUnitName: string;
  buyerType: BuyerType;
  buyerName: string;
  contractType: string;
  contractCapacityMw: number;
  contractRate: number;
  effectiveFrom: string;
  effectiveTo: string;
  pssCode: string;
  pssName: string;
};

type ContractMappingDisplayRow = ContractMappingRow & {
  showCompanyCell: boolean;
  companyRowSpan: number;
  showUnitCell: boolean;
  unitRowSpan: number;
};

type ContractMappingUnit = {
  code: string;
  name: string;
  generatingCompanyCode: string;
  generatingCompanyName: string;
  pssCode: string;
  pssName: string;
};

type ContractMappingMockPayload = {
  header: {
    title: string;
    description: string;
  };
  buyerTypes: BuyerType[];
  contractTypes: string[];
  consumers: ContractMappingOption[];
  utilities: ContractMappingOption[];
  pssOptions: ContractMappingOption[];
  generatingCompanies: ContractMappingOption[];
  generatingUnits: ContractMappingUnit[];
  contractMappings: ContractMappingRow[];
};

type ContractMappingFormValue = {
  generatingCompanyCode: string;
  generatingUnitCode: string;
  buyerType: BuyerType | '';
  buyerName: string;
  contractType: string;
  contractCapacityMw: number | null;
  contractRate: number | null;
  effectiveFrom: string;
  effectiveTo: string;
  pssCode: string;
};

const DEFAULT_DATA: ContractMappingMockPayload = {
  header: {
    title: 'Contract Mapping',
    description: 'Create and review contract mappings for generating companies, units, buyers, and PSS links.'
  },
  buyerTypes: ['Utility', 'Consumer'],
  contractTypes: ['PPA'],
  consumers: [],
  utilities: [],
  pssOptions: [],
  generatingCompanies: [],
  generatingUnits: [],
  contractMappings: []
};

@Component({
  selector: 'app-contract-mapping',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './contract-mapping.component.html',
  styleUrls: ['./contract-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractMappingComponent {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);

  readonly pageData = signal<ContractMappingMockPayload>(DEFAULT_DATA);
  readonly mappingRows = signal<ContractMappingRow[]>([]);
  readonly searchTerm = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(8);
  readonly isFormOpen = signal(false);
  readonly formError = signal<string | null>(null);
  readonly toast = signal<{ message: string; type: 'success' | 'error' } | null>(null);
  readonly selectedGeneratingCompanyCode = signal('');
  readonly selectedGeneratingUnitCode = signal('');
  readonly selectedBuyerType = signal<BuyerType | ''>('');

  readonly title = computed(() => this.route.snapshot.data['title'] as string || this.pageData().header.title);
  readonly description = computed(() => this.route.snapshot.data['description'] as string || this.pageData().header.description);
  readonly buyerTypes = computed(() => this.pageData().buyerTypes);
  readonly contractTypes = computed(() => this.pageData().contractTypes);
  readonly generatingCompanies = computed(() => this.pageData().generatingCompanies);

  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    const rows = this.mappingRows().filter((row) => {
      if (!search) {
        return true;
      }

      return [
        row.generatingCompanyName,
        row.generatingUnitName,
        row.buyerType,
        row.buyerName,
        row.contractType,
        row.pssName,
        String(row.contractCapacityMw),
        String(row.contractRate),
        row.effectiveFrom,
        row.effectiveTo
      ].some((value) => value.toLowerCase().includes(search));
    });

    // Grouped cells use rowspans, so keep company and unit rows contiguous
    // before pagination. New mappings are inserted at the front of the list.
    return rows.sort((left, right) =>
      left.generatingCompanyCode.localeCompare(right.generatingCompanyCode) ||
      left.generatingUnitCode.localeCompare(right.generatingUnitCode) ||
      left.id - right.id
    );
  });

  readonly totalItems = computed(() => this.filteredRows().length);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  readonly displayRows = computed<ContractMappingDisplayRow[]>(() => {
    const rows = this.paginatedRows();
    const companyCounts = new Map<string, number>();
    const unitCounts = new Map<string, number>();

    rows.forEach((row) => {
      companyCounts.set(
        row.generatingCompanyCode,
        (companyCounts.get(row.generatingCompanyCode) || 0) + 1
      );

      const unitKey = `${row.generatingCompanyCode}::${row.generatingUnitCode}`;
      unitCounts.set(unitKey, (unitCounts.get(unitKey) || 0) + 1);
    });

    let previousCompanyCode = '';
    let previousUnitKey = '';

    return rows.map((row) => {
      const unitKey = `${row.generatingCompanyCode}::${row.generatingUnitCode}`;
      const showCompanyCell = row.generatingCompanyCode !== previousCompanyCode;
      const showUnitCell = unitKey !== previousUnitKey;

      previousCompanyCode = row.generatingCompanyCode;
      previousUnitKey = unitKey;

      return {
        ...row,
        showCompanyCell,
        companyRowSpan: showCompanyCell ? companyCounts.get(row.generatingCompanyCode) || 1 : 0,
        showUnitCell,
        unitRowSpan: showUnitCell ? unitCounts.get(unitKey) || 1 : 0
      };
    });
  });
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingText = computed(() => {
    const total = this.totalItems();
    if (total === 0) {
      return 'Showing 0 entries';
    }

    const start = ((this.currentPage() - 1) * this.pageSize()) + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  readonly filteredUnits = computed(() => {
    const companyCode = this.selectedGeneratingCompanyCode();
    return this.pageData().generatingUnits.filter((unit) => !companyCode || unit.generatingCompanyCode === companyCode);
  });

  readonly availablePssOptions = computed(() => {
    const unitCode = this.selectedGeneratingUnitCode();
    if (unitCode) {
      const unit = this.pageData().generatingUnits.find((item) => item.code === unitCode);
      if (unit) {
        return [{ code: unit.pssCode, name: unit.pssName }];
      }
    }

    const companyCode = this.selectedGeneratingCompanyCode();
    if (!companyCode) {
      return this.pageData().pssOptions;
    }

    const seen = new Map<string, ContractMappingOption>();
    this.pageData().generatingUnits
      .filter((unit) => unit.generatingCompanyCode === companyCode)
      .forEach((unit) => {
        seen.set(unit.pssCode, { code: unit.pssCode, name: unit.pssName });
      });
    return Array.from(seen.values());
  });

  readonly availableBuyerOptions = computed(() => {
    const buyerType = this.selectedBuyerType();
    if (buyerType === 'Utility') {
      return this.pageData().utilities;
    }
    if (buyerType === 'Consumer') {
      return this.pageData().consumers;
    }
    return [];
  });

  readonly form = this.fb.nonNullable.group({
    generatingCompanyCode: ['', Validators.required],
    generatingUnitCode: ['', Validators.required],
    buyerType: ['' as BuyerType | '', Validators.required],
    buyerName: ['', Validators.required],
    contractType: ['', Validators.required],
    contractCapacityMw: [null as number | null, [Validators.required, Validators.min(0.01)]],
    contractRate: [null as number | null, [Validators.required, Validators.min(0.01)]],
    effectiveFrom: ['', Validators.required],
    effectiveTo: ['', Validators.required],
    pssCode: ['', Validators.required]
  });

  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.http.get<ContractMappingMockPayload>('json/contract-mapping.mock.json').subscribe({
      next: (data) => {
        this.pageData.set(data);
        this.mappingRows.set(data.contractMappings);
      },
      error: () => {
        this.pageData.set(DEFAULT_DATA);
        this.mappingRows.set([]);
        this.showToast('Unable to load contract mapping mock data.', 'error');
      }
    });

    this.form.controls.generatingCompanyCode.valueChanges.subscribe((companyCode) => {
      this.selectedGeneratingCompanyCode.set(companyCode || '');
      const selectedUnitCode = this.form.controls.generatingUnitCode.value;
      const nextUnits = this.pageData().generatingUnits.filter((unit) => unit.generatingCompanyCode === companyCode);
      if (selectedUnitCode && !nextUnits.some((unit) => unit.code === selectedUnitCode)) {
        this.form.patchValue({ generatingUnitCode: '', pssCode: '' }, { emitEvent: false });
        this.selectedGeneratingUnitCode.set('');
      } else if (!companyCode) {
        this.form.patchValue({ generatingUnitCode: '', pssCode: '' }, { emitEvent: false });
        this.selectedGeneratingUnitCode.set('');
      }
    });

    this.form.controls.generatingUnitCode.valueChanges.subscribe((unitCode) => {
      this.selectedGeneratingUnitCode.set(unitCode || '');
      const unit = this.pageData().generatingUnits.find((item) => item.code === unitCode);
      this.form.patchValue({ pssCode: unit?.pssCode || '' }, { emitEvent: false });
    });

    this.form.controls.buyerType.valueChanges.subscribe((buyerType) => {
      this.selectedBuyerType.set((buyerType || '') as BuyerType | '');
      this.form.patchValue({ buyerName: '' }, { emitEvent: false });
    });
  }

  openCreateForm(): void {
    this.resetForm();
    this.isFormOpen.set(true);
  }

  closeForm(): void {
    this.isFormOpen.set(false);
    this.resetForm();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.formError.set('Please fill all required fields before saving.');
      return;
    }

    const rawValue = this.form.getRawValue() as ContractMappingFormValue;
    if (rawValue.effectiveFrom > rawValue.effectiveTo) {
      this.formError.set('Effective From date must be earlier than or equal to Effective To date.');
      return;
    }

    const company = this.pageData().generatingCompanies.find((item) => item.code === rawValue.generatingCompanyCode);
    const unit = this.pageData().generatingUnits.find((item) => item.code === rawValue.generatingUnitCode);
    const pss = this.pageData().pssOptions.find((item) => item.code === rawValue.pssCode);

    if (!company || !unit || !pss) {
      this.formError.set('Selected mapping values are not valid for the available mock data.');
      return;
    }

    const nextRow: ContractMappingRow = {
      id: this.nextRowId(),
      generatingCompanyCode: company.code,
      generatingCompanyName: company.name,
      generatingUnitCode: unit.code,
      generatingUnitName: unit.name,
      buyerType: rawValue.buyerType as BuyerType,
      buyerName: rawValue.buyerName,
      contractType: rawValue.contractType,
      contractCapacityMw: Number(rawValue.contractCapacityMw),
      contractRate: Number(rawValue.contractRate),
      effectiveFrom: rawValue.effectiveFrom,
      effectiveTo: rawValue.effectiveTo,
      pssCode: pss.code,
      pssName: pss.name
    };

    this.mappingRows.update((rows) => [nextRow, ...rows]);
    this.currentPage.set(1);
    this.closeForm();
    this.showToast('Contract mapping added successfully.', 'success');
  }

  updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  formatNumber(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  optionLabel(code: string, options: ContractMappingOption[]): string {
    return options.find((option) => option.code === code)?.name || code;
  }

  private nextRowId(): number {
    return this.mappingRows().reduce((maxId, row) => Math.max(maxId, row.id), 0) + 1;
  }

  private resetForm(): void {
    this.selectedGeneratingCompanyCode.set('');
    this.selectedGeneratingUnitCode.set('');
    this.selectedBuyerType.set('');
    this.form.reset({
      generatingCompanyCode: '',
      generatingUnitCode: '',
      buyerType: '',
      buyerName: '',
      contractType: '',
      contractCapacityMw: null,
      contractRate: null,
      effectiveFrom: '',
      effectiveTo: '',
      pssCode: ''
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.formError.set(null);
  }

  private showToast(message: string | null, type: 'success' | 'error' = 'success'): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    if (!message) {
      this.toast.set(null);
      return;
    }

    this.toast.set({ message, type });
    this.toastTimer = setTimeout(() => {
      this.toast.set(null);
      this.toastTimer = null;
    }, 3200);
  }
}
